import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useApp } from "../state/AppContext";
import { useLabels, useViews } from "../state/hooks";
import { Toggle } from "./Toggle";
import type { TeamMember, MailboxRole, CustomViewRule } from "../../shared/types";
import { CUSTOM_VIEW_RULES } from "../../shared/types";

export type Pane =
  | "account"
  | "team"
  | "views"
  | "labels"
  | "claude"
  | "signature"
  | "vacation"
  | "spam"
  | "appearance"
  | "notifications"
  | "shortcuts";

export const SETTINGS_PANES: { key: Pane; label: string }[] = [
  { key: "account", label: "Account" },
  { key: "team", label: "Team access" },
  { key: "views", label: "Views" },
  { key: "labels", label: "Labels & colors" },
  { key: "claude", label: "Claude connector" },
  { key: "signature", label: "Signature" },
  { key: "vacation", label: "Vacation reply" },
  { key: "spam", label: "Spam screening" },
  { key: "appearance", label: "Appearance" },
  { key: "notifications", label: "Notifications" },
  { key: "shortcuts", label: "Shortcuts" },
];

export function Settings({ onClose, initialPane }: { onClose: () => void; initialPane?: Pane }) {
  const { me, currentMailbox } = useApp();
  const [pane, setPane] = useState<Pane>(initialPane ?? "account");

  if (!me || !currentMailbox) return null;

  return (
    <div className="gm-modal-scrim" onClick={onClose}>
      <div className="gm-settings-modal gm-in" onClick={(e) => e.stopPropagation()}>
        <div className="gm-settings-rail">
          {SETTINGS_PANES.map((p) => (
            <button key={p.key} className="gm-nav-row" data-active={pane === p.key} onClick={() => setPane(p.key)}>
              {p.label}
            </button>
          ))}
          <button className="gm-nav-row" style={{ marginTop: "auto" }} onClick={() => api.post("/auth/logout").then(() => location.reload())}>
            Sign out
          </button>
        </div>
        <div className="gm-settings-main">
          <div className="gm-settings-header">
            <div style={{ fontSize: 15, fontWeight: 500 }}>{currentMailbox.displayName ?? currentMailbox.email}</div>
            <button className="btn btn-ghost" style={{ marginLeft: "auto" }} onClick={onClose}>Esc</button>
          </div>
          <div className="gm-settings-body">
            {pane === "account" && <AccountPane />}
            {pane === "team" && <TeamPane mailboxId={currentMailbox.id} myRole={currentMailbox.role} />}
            {pane === "views" && <ViewsPane mailboxId={currentMailbox.id} />}
            {pane === "labels" && <LabelsPane mailboxId={currentMailbox.id} />}
            {pane === "claude" && <ClaudePane />}
            {pane === "signature" && <SignaturePane mailboxId={currentMailbox.id} />}
            {pane === "vacation" && <VacationPane mailboxId={currentMailbox.id} />}
            {pane === "spam" && <SpamPane mailboxId={currentMailbox.id} />}
            {pane === "appearance" && <AppearancePane />}
            {pane === "notifications" && <NotificationsPane />}
            {pane === "shortcuts" && <ShortcutsPane />}
          </div>
        </div>
      </div>
    </div>
  );
}

interface VoiceInfo {
  styleNotes: string | null;
  learnedAt: string | null;
  templates: { id: string; label: string }[];
}

function AccountPane() {
  const { me, currentMailbox } = useApp();
  const [voice, setVoice] = useState<VoiceInfo | null>(null);
  const [learning, setLearning] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!currentMailbox) return;
    api.get<VoiceInfo>(`/api/mailboxes/${currentMailbox.id}/voice`).then(setVoice);
  }, [currentMailbox]);

  async function learnVoice() {
    if (!currentMailbox) return;
    setLearning(true);
    setVoiceMessage(null);
    try {
      const res = await api.post<{ templateCount: number; sampledMessages: number }>(`/api/mailboxes/${currentMailbox.id}/voice`);
      setVoiceMessage(`Learned from ${res.sampledMessages} sent messages, found ${res.templateCount} templates.`);
      const fresh = await api.get<VoiceInfo>(`/api/mailboxes/${currentMailbox.id}/voice`);
      setVoice(fresh);
    } catch (e) {
      setVoiceMessage((e as Error).message);
    } finally {
      setLearning(false);
    }
  }

  if (!me) return null;
  return (
    <>
      <div className="gm-setting-row">
        <div className="avatar" style={{ width: 44, height: 44, borderRadius: 10, fontSize: 16 }}>
          {me.user.name.slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="gm-setting-label">{me.user.name}</div>
          <div className="gm-setting-detail">{me.user.email}</div>
        </div>
      </div>
      <div className="section-header" style={{ marginTop: 8 }}>Connected mailboxes</div>
      {me.mailboxes.map((m) => (
        <div className="gm-setting-row" key={m.id}>
          <div>
            <div className="gm-setting-label">{m.email}</div>
            <div className="gm-setting-detail">{m.kind === "shared" ? "Shared team inbox" : "Personal inbox"} · {m.status}</div>
          </div>
          <div className="gm-setting-control">
            <span className="gm-role-pill">{m.role}</span>
          </div>
        </div>
      ))}
      <a className="btn" href="/auth/connect-mailbox" style={{ alignSelf: "flex-start", marginTop: 8 }}>
        Connect a mailbox
      </a>

      {currentMailbox && (
        <>
          <div className="section-header" style={{ marginTop: 8 }}>Drafting voice</div>
          <div className="gm-setting-row">
            <div>
              <div className="gm-setting-label">
                {voice?.learnedAt ? `Learned on ${new Date(voice.learnedAt).toLocaleDateString()}` : "Not learned yet"}
              </div>
              <div className="gm-setting-detail">
                {voice?.templates.length ? `${voice.templates.length} templates found` : "Reads recent sent mail to match your tone in reply drafts"}
              </div>
            </div>
            <div className="gm-setting-control">
              <button className="btn" disabled={learning} onClick={learnVoice}>
                {learning ? "Learning…" : voice?.learnedAt ? "Re-learn" : "Learn from sent mail"}
              </button>
            </div>
          </div>
          {voiceMessage && <div style={{ fontSize: 11.5, color: "var(--gm-text-4)" }}>{voiceMessage}</div>}
        </>
      )}
    </>
  );
}

function TeamPane({ mailboxId, myRole }: { mailboxId: string; myRole: MailboxRole }) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MailboxRole>("agent");
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    const res = await api.get<{ members: TeamMember[] }>(`/api/mailboxes/${mailboxId}/team`);
    setMembers(res.members);
  }
  useEffect(() => {
    reload();
  }, [mailboxId]);

  async function invite() {
    setError(null);
    try {
      await api.post(`/api/mailboxes/${mailboxId}/team`, { email, role });
      setEmail("");
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      {members.map((m) => (
        <div className="gm-setting-row" key={m.user.id}>
          <div>
            <div className="gm-setting-label">{m.user.name}</div>
            <div className="gm-setting-detail">{m.user.email}</div>
          </div>
          <div className="gm-setting-control">
            {myRole === "owner" ? (
              <select className="input" style={{ width: "auto" }} value={m.role} onChange={(e) => api.patch(`/api/mailboxes/${mailboxId}/team/${m.user.id}`, { role: e.target.value }).then(reload)}>
                <option value="owner">Owner</option>
                <option value="agent">Agent</option>
                <option value="read_only">Read only</option>
              </select>
            ) : (
              <span className="gm-role-pill">{m.role}</span>
            )}
            {myRole === "owner" && m.role !== "owner" && (
              <button className="btn btn-ghost btn-danger" onClick={() => api.delete(`/api/mailboxes/${mailboxId}/team/${m.user.id}`).then(reload)}>
                Remove
              </button>
            )}
          </div>
        </div>
      ))}
      {myRole === "owner" && (
        <div className="gm-setting-row">
          <input className="input" placeholder="name@geertech.net" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select className="input" style={{ width: "auto" }} value={role} onChange={(e) => setRole(e.target.value as MailboxRole)}>
            <option value="agent">Agent</option>
            <option value="read_only">Read only</option>
            <option value="owner">Owner</option>
          </select>
          <button className="btn btn-primary" onClick={invite}>Send invite</button>
        </div>
      )}
      {error && <div style={{ color: "var(--lb-red-2)", fontSize: 12 }}>{error}</div>}
    </>
  );
}

const RULE_LABELS: Record<CustomViewRule, string> = {
  assigned_to_me: "Assigned to me",
  from_vendors: "From vendors",
  needs_a_reply: "Needs a reply",
  has_a_draft: "Has a draft",
  snoozed_or_waiting: "Snoozed or waiting",
};

function ViewsPane({ mailboxId }: { mailboxId: string }) {
  const { views, reload } = useViews(mailboxId);
  const [name, setName] = useState("");
  const [rule, setRule] = useState<CustomViewRule>("assigned_to_me");

  return (
    <>
      {views.map((v) => (
        <div className="gm-setting-row" key={v.id}>
          <div>
            <div className="gm-setting-label">{v.name}</div>
            <div className="gm-setting-detail">{v.builtinKey ? "Built in" : `Custom · ${RULE_LABELS[v.rule as CustomViewRule] ?? v.rule}`}</div>
          </div>
          <div className="gm-setting-control">
            <span style={{ fontSize: 11, color: "var(--gm-text-4)" }}>{v.hidden ? "Hidden" : "Shown"}</span>
            <Toggle on={!v.hidden} onChange={(next) => api.patch(`/api/mailboxes/${mailboxId}/views/${v.id}`, { hidden: !next }).then(reload)} />
            {!v.builtinKey && (
              <button className="btn btn-ghost btn-danger" onClick={() => api.delete(`/api/mailboxes/${mailboxId}/views/${v.id}`).then(reload)}>
                Delete
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="gm-setting-row">
        <input className="input" placeholder="View name" value={name} onChange={(e) => setName(e.target.value)} />
        <select className="input" style={{ width: "auto" }} value={rule} onChange={(e) => setRule(e.target.value as CustomViewRule)}>
          {CUSTOM_VIEW_RULES.map((r) => (
            <option key={r} value={r}>{RULE_LABELS[r]}</option>
          ))}
        </select>
        <button
          className="btn btn-primary"
          onClick={() => {
            if (!name.trim()) return;
            api.post(`/api/mailboxes/${mailboxId}/views`, { name, rule }).then(() => {
              setName("");
              reload();
            });
          }}
        >
          Add
        </button>
      </div>
    </>
  );
}

const SWATCHES = [
  "var(--lb-red-2)", "var(--lb-orange-2)", "var(--lb-amber-2)", "var(--lb-green-2)",
  "var(--lb-teal-2)", "var(--lb-blue-2)", "var(--lb-violet-2)", "var(--lb-magenta-2)",
];

function LabelsPane({ mailboxId }: { mailboxId: string }) {
  const { labels, reload } = useLabels(mailboxId);
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[0]!);

  return (
    <>
      {labels.map((l) => (
        <div className="gm-setting-row" key={l.id}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />
          <div>
            <div className="gm-setting-label">{l.name}</div>
            <div className="gm-setting-detail">{l.threadCount} threads{l.parentLabelId ? " · nested" : ""}</div>
          </div>
          <div className="gm-setting-control">
            <button className="btn btn-ghost btn-danger" onClick={() => api.delete(`/api/mailboxes/${mailboxId}/labels/${l.id}`).then(reload)}>
              Delete
            </button>
          </div>
        </div>
      ))}
      <div className="gm-setting-row">
        <div style={{ display: "flex", gap: 4 }}>
          {SWATCHES.map((s) => (
            <button
              key={s}
              onClick={() => setColor(s)}
              style={{ width: 18, height: 18, borderRadius: 4, background: s, border: color === s ? "2px solid var(--gm-text)" : "none", cursor: "pointer" }}
            />
          ))}
        </div>
        <input className="input" placeholder="Label name" value={name} onChange={(e) => setName(e.target.value)} />
        <button
          className="btn btn-primary"
          onClick={() => {
            if (!name.trim()) return;
            api.post(`/api/mailboxes/${mailboxId}/labels`, { name, color }).then(() => {
              setName("");
              reload();
            });
          }}
        >
          Create
        </button>
      </div>
    </>
  );
}

function ClaudePane() {
  return (
    <div className="gm-setting-row">
      <div>
        <div className="gm-setting-label">Claude · claude-sonnet-5</div>
        <div className="gm-setting-detail">
          Summaries, reply directions, triage, and spam reasoning run server-side using a workspace-wide API key — there's no personal key to
          manage here. If a thread isn't showing a summary, the key may not be configured yet, or generation is temporarily unavailable.
        </div>
      </div>
    </div>
  );
}

function SignaturePane({ mailboxId }: { mailboxId: string }) {
  const [signature, setSignature] = useState("");
  const [saved, setSaved] = useState(true);

  useEffect(() => {
    api.get<{ signature: string }>(`/api/mailboxes/${mailboxId}/settings/signature`).then((r) => setSignature(r.signature));
  }, [mailboxId]);

  return (
    <>
      <div className="gm-setting-detail">Stored directly in Gmail — this is the same signature you'd see in Gmail's own settings.</div>
      <textarea className="input" style={{ minHeight: 140 }} value={signature} onChange={(e) => { setSignature(e.target.value); setSaved(false); }} />
      <button
        className="btn btn-primary"
        style={{ alignSelf: "flex-start" }}
        onClick={() => api.patch(`/api/mailboxes/${mailboxId}/settings/signature`, { signature }).then(() => setSaved(true))}
      >
        {saved ? "Saved" : "Save"}
      </button>
    </>
  );
}

interface Vacation {
  enableAutoReply: boolean;
  responseSubject?: string;
  responseBodyPlainText?: string;
  restrictToContacts?: boolean;
}

function VacationPane({ mailboxId }: { mailboxId: string }) {
  const [vac, setVac] = useState<Vacation>({ enableAutoReply: false });

  useEffect(() => {
    api.get<Vacation>(`/api/mailboxes/${mailboxId}/settings/vacation`).then(setVac);
  }, [mailboxId]);

  function save(next: Vacation) {
    setVac(next);
    api.put(`/api/mailboxes/${mailboxId}/settings/vacation`, next);
  }

  return (
    <>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Vacation responder</div>
        <div className="gm-setting-control">
          <Toggle on={vac.enableAutoReply} onChange={(on) => save({ ...vac, enableAutoReply: on })} />
        </div>
      </div>
      <input className="input" placeholder="Subject" value={vac.responseSubject ?? ""} onChange={(e) => save({ ...vac, responseSubject: e.target.value })} />
      <textarea className="input" style={{ minHeight: 120 }} placeholder="Message" value={vac.responseBodyPlainText ?? ""} onChange={(e) => save({ ...vac, responseBodyPlainText: e.target.value })} />
      <div className="gm-setting-row">
        <div className="gm-setting-label">Only reply to people in Contacts</div>
        <div className="gm-setting-control">
          <Toggle on={!!vac.restrictToContacts} onChange={(on) => save({ ...vac, restrictToContacts: on })} />
        </div>
      </div>
    </>
  );
}

interface SpamRoutine {
  enabled: boolean;
  startDate: string | null;
  timeOfDay: string | null;
  repeat: "daily" | "weekly" | "monthly" | null;
  waitingCount: number;
  blockedSenderCount: number;
}

function SpamPane({ mailboxId }: { mailboxId: string }) {
  const [routine, setRoutine] = useState<SpamRoutine | null>(null);

  useEffect(() => {
    api.get<SpamRoutine>(`/api/mailboxes/${mailboxId}/settings/spam-routine`).then(setRoutine);
  }, [mailboxId]);

  if (!routine) return null;

  function save(patch: Partial<SpamRoutine>) {
    const next = { ...routine!, ...patch };
    setRoutine(next);
    api.patch(`/api/mailboxes/${mailboxId}/settings/spam-routine`, next);
  }

  return (
    <>
      <div className="gm-setting-row">
        <div>
          <div className="gm-setting-label">Screen for spam on a schedule</div>
          <div className="gm-setting-detail">{routine.waitingCount} waiting · {routine.blockedSenderCount} blocked senders</div>
        </div>
        <div className="gm-setting-control">
          <Toggle on={routine.enabled} onChange={(on) => save({ enabled: on })} />
        </div>
      </div>
      {routine.enabled && (
        <div className="gm-setting-row">
          <input className="input" type="date" value={routine.startDate ?? ""} onChange={(e) => save({ startDate: e.target.value })} />
          <input className="input" type="time" value={routine.timeOfDay ?? ""} onChange={(e) => save({ timeOfDay: e.target.value })} />
          <select className="input" style={{ width: "auto" }} value={routine.repeat ?? "weekly"} onChange={(e) => save({ repeat: e.target.value as SpamRoutine["repeat"] })}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      )}
    </>
  );
}

function AppearancePane() {
  const { me, updatePrefs } = useApp();
  if (!me) return null;
  const { prefs } = me;

  return (
    <>
      <div style={{ display: "flex", gap: 11.2 }}>
        {(["graphite", "paper"] as const).map((t) => (
          <button
            key={t}
            className="card"
            style={{ cursor: "pointer", border: prefs.theme === t ? "1px solid var(--gm-accent-deep)" : "1px solid transparent", textTransform: "capitalize" }}
            onClick={() => updatePrefs({ theme: t })}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Undo send</div>
        <div className="gm-setting-control">
          <select className="input" style={{ width: "auto" }} value={prefs.undoWindowSeconds} onChange={(e) => updatePrefs({ undoWindowSeconds: Number(e.target.value) as 0 | 15 | 30 })}>
            <option value={0}>Off</option>
            <option value={15}>15 seconds</option>
            <option value={30}>30 seconds</option>
          </select>
        </div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Dense rows</div>
        <div className="gm-setting-control"><Toggle on={prefs.denseRows} onChange={(on) => updatePrefs({ denseRows: on })} /></div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Auto-advance after archive or send</div>
        <div className="gm-setting-control"><Toggle on={prefs.autoAdvance} onChange={(on) => updatePrefs({ autoAdvance: on })} /></div>
      </div>
    </>
  );
}

function NotificationsPane() {
  const { me, updatePrefs } = useApp();
  if (!me) return null;
  const { prefs } = me;

  return (
    <>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Desktop notifications</div>
        <div className="gm-setting-control"><Toggle on={prefs.notifDesktop} onChange={(on) => updatePrefs({ notifDesktop: on })} /></div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">@mentions</div>
        <div className="gm-setting-control"><Toggle on={prefs.notifMentions} onChange={(on) => updatePrefs({ notifMentions: on })} /></div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Sound</div>
        <div className="gm-setting-control"><Toggle on={prefs.notifSound} onChange={(on) => updatePrefs({ notifSound: on })} /></div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Morning digest</div>
        <div className="gm-setting-control"><Toggle on={prefs.notifMorningDigest} onChange={(on) => updatePrefs({ notifMorningDigest: on })} /></div>
      </div>
      <div className="gm-setting-row">
        <div className="gm-setting-label">Quiet hours</div>
        <div className="gm-setting-control">
          <input className="input" type="time" style={{ width: "auto" }} value={prefs.quietHoursStart ?? ""} onChange={(e) => updatePrefs({ quietHoursStart: e.target.value })} />
          <span>–</span>
          <input className="input" type="time" style={{ width: "auto" }} value={prefs.quietHoursEnd ?? ""} onChange={(e) => updatePrefs({ quietHoursEnd: e.target.value })} />
        </div>
      </div>
    </>
  );
}

const SHORTCUTS: [string, string][] = [
  ["⌘K", "Command palette"], ["⌘,", "Settings"], ["⌘J", "Toggle Claude panel"], ["⌘⏎", "Send"],
  ["C", "Compose"], ["R", "Reply"], ["A", "Assign"], ["S", "Snooze"],
  ["E", "Archive"], ["L", "Label picker"], ["/", "Focus search"], ["J / W", "Next / previous thread"],
  ["Delete", "Delete thread"], ["Esc", "Close all panels"],
];

function ShortcutsPane() {
  return (
    <div className="gm-shortcut-grid">
      {SHORTCUTS.map(([key, desc]) => (
        <div className="gm-shortcut-row" key={key}>
          <span className="gm-key-chip">{key}</span>
          <span>{desc}</span>
        </div>
      ))}
    </div>
  );
}
